// src/components/builder/topicMediaStatus.js
//
// What a topic will REALLY show on a portal page — for the little V / S /
// SA / L / G lights in the Layout Builder.
//
// WHY THIS EXISTS
// ---------------
// The lights used to read only what was typed into the page itself
// (videoUrl, musicUrl, quizId, inline questions). The live page
// (DynamicPortalPage) shows far more than that:
//
//   * Video, song, sing-along and lyrics come from the TOPIC LIBRARY when
//     the page leaves them blank (mergeTopicWithLibrary — published library
//     topics only).
//   * The Game button shows when the topic has questions ANYWHERE: inline,
//     a quizzes/{slug} document in Firestore (the Game generator publishes
//     there), or the built-in quiz library (src/data/quiz-library).
//
// So a topic could have a working game for every student and still show a
// grey G here. This module answers the same questions the live page asks,
// the same way, so the builder and the classroom agree.
//
// Each light is one of:
//   "on"    — students see it
//   "draft" — it's in the Topic Library, but the topic there is a Draft, so
//             students DON'T see it until it's published
//   "off"   — nothing
//
// Data is loaded once (topicLibrary + quizzes) and shared by every light on
// the page; it reloads when it's more than a minute old and a light mounts,
// and when another panel announces a topic change (sbl:topic-updated).

import { useEffect, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase/firebaseConfig";
import { getAllLibraryTopics } from "../../services/topicLibraryService";
import { hasQuiz } from "../../data/quiz-library";

/** Lyrics need MORE than this many sung lines for the L light. */
export const LYRICS_MIN_LINES = 10;

const STALE_MS = 60 * 1000;

const state = {
  library: {}, // lowercase id/slug -> topic
  quizSlugs: new Set(), // lowercase slugs with questions in Firestore
  loadedAt: 0,
  loading: null,
};
const listeners = new Set();
const notify = () => listeners.forEach((fn) => fn(state.loadedAt));

const load = async () => {
  if (state.loading) return state.loading;
  state.loading = (async () => {
    try {
      const [topics, quizSnap] = await Promise.all([
        getAllLibraryTopics().catch(() => []),
        getDocs(collection(db, "quizzes")).catch(() => null),
      ]);
      const lib = {};
      topics.forEach((t) => {
        if (t.id) lib[String(t.id).toLowerCase()] = t;
        if (t.slug) lib[String(t.slug).toLowerCase()] = t;
      });
      const quiz = new Set();
      if (quizSnap) {
        quizSnap.forEach((d) => {
          if ((d.data()?.questions || []).length) quiz.add(d.id.toLowerCase());
        });
      }
      state.library = lib;
      state.quizSlugs = quiz;
      state.loadedAt = Date.now();
    } finally {
      state.loading = null;
      notify();
    }
  })();
  return state.loading;
};

/** Force a reload (e.g. after saving a topic). */
export const refreshTopicMediaStatus = () => {
  state.loadedAt = 0;
  return load();
};

if (typeof window !== "undefined") {
  window.addEventListener("sbl:topic-updated", (e) => {
    const { id, patch } = e.detail || {};
    const t = id && state.library[String(id).toLowerCase()];
    if (!t || !patch) return;
    const next = { ...t, ...patch };
    Object.keys(state.library).forEach((k) => {
      if (state.library[k] === t) state.library[k] = next;
    });
    notify();
  });
}

/** Subscribes a component to the shared data; returns a version number. */
export const useTopicMediaData = () => {
  const [version, setVersion] = useState(state.loadedAt);
  useEffect(() => {
    listeners.add(setVersion);
    if (!state.loadedAt || Date.now() - state.loadedAt > STALE_MS) load();
    return () => listeners.delete(setVersion);
  }, []);
  return version;
};

/** Sung lines: not blank, not a "(Chorus)" heading, not the © credit. */
export const sungLineCount = (lyrics) =>
  (Array.isArray(lyrics) ? lyrics : String(lyrics || "").split("\n"))
    .map((l) => String(l ?? "").trim())
    .filter((l) => l && !/^[([][^)\]]{1,40}[)\]]\s*:?$/.test(l) && !/^©|all rights reserved/i.test(l)).length;

/**
 * The five lights for one page topic.
 * Returns [{ key, letter, label, state, detail }].
 */
export const topicMediaStatus = (topic = {}) => {
  const slug = String(topic.slug || "").toLowerCase();
  const lib = slug ? state.library[slug] : null;
  const libLive = lib?.status === "published";
  const loaded = state.loadedAt > 0;

  // own value on the page wins; otherwise the library fills the blank
  const pick = (own, libValue) => {
    if (own) return { state: "on", from: "page" };
    if (libValue) return { state: libLive ? "on" : "draft", from: "library" };
    return { state: "off", from: "" };
  };

  const video = pick(topic.videoUrl || topic.video, lib?.videoUrl);
  const song = pick(topic.musicUrl || topic.music, lib?.musicUrl);
  const sing = pick(topic.singAlongUrl, lib?.singAlongUrl);

  const ownLines = sungLineCount(topic.lyrics);
  const libLines = sungLineCount(lib?.lyrics);
  const lyrics =
    ownLines > LYRICS_MIN_LINES
      ? { state: "on", from: "page", n: ownLines }
      : libLines > LYRICS_MIN_LINES
      ? { state: libLive ? "on" : "draft", from: "library", n: libLines }
      : { state: "off", from: "", n: Math.max(ownLines, libLines) };

  let gameFrom = "";
  if ((topic.questions || []).length > 0) gameFrom = "questions on this page";
  else if (topic.quizId) gameFrom = "a linked quiz";
  else if (slug && state.quizSlugs.has(slug)) gameFrom = "the game library (Firestore)";
  else if (slug && hasQuiz(topic.slug)) gameFrom = "the built-in quiz library";
  const gameType = topic.gameType || lib?.gameType || "multipleChoice";

  const why = (s, from, what) =>
    s === "on"
      ? `${what}: yes${from === "library" ? " (from the Topic Library)" : ""}`
      : s === "draft"
      ? `${what}: in the Topic Library, but that topic is a DRAFT — publish it so students see it`
      : `${what}: missing`;

  return [
    { key: "video", letter: "V", label: "Video", state: video.state, detail: why(video.state, video.from, "Video") },
    { key: "music", letter: "S", label: "Song", state: song.state, detail: why(song.state, song.from, "Song") },
    {
      key: "singalong",
      letter: "SA",
      label: "Sing-along",
      state: sing.state,
      detail: why(sing.state, sing.from, "Sing-along video"),
    },
    {
      key: "lyrics",
      letter: "L",
      label: "Lyrics",
      state: lyrics.state,
      detail:
        lyrics.state === "off"
          ? `Lyrics: ${lyrics.n ? `only ${lyrics.n} line${lyrics.n === 1 ? "" : "s"}` : "none"} — needs more than ${LYRICS_MIN_LINES}`
          : `${why(lyrics.state, lyrics.from, "Lyrics")} · ${lyrics.n} lines`,
    },
    {
      key: "game",
      letter: "G",
      label: "Game",
      state: gameFrom ? "on" : "off",
      detail: gameFrom
        ? `Game: yes — ${gameType}, questions from ${gameFrom}`
        : loaded
        ? `Game: set to ${gameType}, but there are no questions for "${topic.slug || "this topic"}" yet — make some in the Game generator`
        : "Game: checking…",
    },
  ];
};
